import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Eye, EyeOff, Star, Loader2, EyeOff as ArchiveIcon, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * CurationPanel — admin-only bulk curation tool.
 *
 * Two tables, side-by-side:
 *   1. Tunes  — toggle Hidden + Public Example flags per filename
 *   2. Datalogs — toggle Hidden flag per datalog id (across the admin's vehicles)
 *
 * Hidden = faded out in the admin's own timeline, hidden completely from
 * non-admin users. Example = the only thing anonymous visitors can see in
 * the public Tune Timeline (default off — public sees nothing until you opt in).
 */
export default function CurationPanel({ vehicleId }) {
  const [tunes, setTunes] = useState([]);
  const [datalogs, setDatalogs] = useState([]);
  const [showHiddenTunes, setShowHiddenTunes] = useState(true);
  const [showHiddenDatalogs, setShowHiddenDatalogs] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    fetch(`${API_URL}/api/tune-versions`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(setTunes)
      .catch(() => setTunes([]));
  }, [refreshTick]);

  useEffect(() => {
    if (!vehicleId) return;
    fetch(`${API_URL}/api/datalogs/${vehicleId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(setDatalogs)
      .catch(() => setDatalogs([]));
  }, [vehicleId, refreshTick]);

  const patchTune = useCallback(async (filename, patch) => {
    setSavingId(filename);
    try {
      const res = await fetch(`${API_URL}/api/admin/tune-versions/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ filename, ...patch }),
      });
      if (res.ok) setRefreshTick(t => t + 1);
    } finally {
      setSavingId(null);
    }
  }, []);

  const patchDatalog = useCallback(async (id, hidden) => {
    setSavingId(id);
    try {
      const res = await fetch(`${API_URL}/api/admin/datalogs/${id}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ hidden }),
      });
      if (res.ok) setRefreshTick(t => t + 1);
    } finally {
      setSavingId(null);
    }
  }, []);

  const visibleTunes = showHiddenTunes ? tunes : tunes.filter(t => !t.hidden);
  const visibleDatalogs = showHiddenDatalogs ? datalogs : datalogs.filter(d => !d.hidden);
  const tuneStats = {
    total: tunes.length,
    hidden: tunes.filter(t => t.hidden).length,
    examples: tunes.filter(t => t.is_example).length,
  };
  const datalogStats = {
    total: datalogs.length,
    hidden: datalogs.filter(d => d.hidden).length,
  };

  return (
    <Card className="bg-[#111111] border-[#27272A] rounded-sm" data-testid="curation-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
          <Filter className="w-5 h-5 text-[#FF5E1A]" />Curation
        </CardTitle>
        <CardDescription className="text-zinc-500 text-xs">
          Hide low-quality records without deleting them. Star = "Public Example" (the only thing
          anonymous visitors will see). Eye = "Hidden" (faded in your timeline, removed for non-admin users).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* === TUNES === */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono']">
              Tunes — {tuneStats.total} total · {tuneStats.examples} public · {tuneStats.hidden} hidden
            </p>
            <label className="text-[10px] text-zinc-400 inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={showHiddenTunes}
                onChange={(e) => setShowHiddenTunes(e.target.checked)}
                className="accent-[#FF5E1A]"
                data-testid="toggle-show-hidden-tunes"
              />
              Show hidden
            </label>
          </div>
          <div className="border border-[#27272A] rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#050505] text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-wider">Filename</th>
                  <th className="text-center px-3 py-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-wider">Version</th>
                  <th className="text-center px-3 py-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-wider w-20" title="Public Example — visible to anonymous visitors">
                    Public
                  </th>
                  <th className="text-center px-3 py-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-wider w-20" title="Hidden — faded in your timeline, removed for non-admin">
                    Hidden
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleTunes.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-zinc-600">No tunes</td></tr>
                )}
                {visibleTunes.map((t) => (
                  <tr key={t.filename} className={`border-t border-[#1a1a1a] ${t.hidden ? 'opacity-40' : ''}`}>
                    <td className="px-3 py-2 font-['JetBrains_Mono'] text-zinc-300 truncate max-w-[280px]" title={t.filename}>
                      {t.filename}
                    </td>
                    <td className="px-3 py-2 text-center text-zinc-400">{t.version || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => patchTune(t.filename, { is_example: !t.is_example })}
                        disabled={savingId === t.filename}
                        className={`p-1 rounded-sm hover:bg-[#1a1a1a] ${t.is_example ? 'text-amber-300' : 'text-zinc-600 hover:text-amber-400'}`}
                        data-testid={`curation-toggle-example-${t.filename}`}
                        title={t.is_example ? 'Public example — click to hide from anonymous' : 'Click to make public example'}
                      >
                        {savingId === t.filename ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Star className="w-4 h-4" fill={t.is_example ? 'currentColor' : 'none'} />}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => patchTune(t.filename, { hidden: !t.hidden })}
                        disabled={savingId === t.filename}
                        className={`p-1 rounded-sm hover:bg-[#1a1a1a] ${t.hidden ? 'text-zinc-500' : 'text-zinc-600 hover:text-zinc-300'}`}
                        data-testid={`curation-toggle-hidden-${t.filename}`}
                        title={t.hidden ? 'Hidden — click to unhide' : 'Click to hide'}
                      >
                        {t.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* === DATALOGS === */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono']">
              Datalogs — {datalogStats.total} total · {datalogStats.hidden} hidden
            </p>
            <label className="text-[10px] text-zinc-400 inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={showHiddenDatalogs}
                onChange={(e) => setShowHiddenDatalogs(e.target.checked)}
                className="accent-[#FF5E1A]"
                data-testid="toggle-show-hidden-datalogs"
              />
              Show hidden
            </label>
          </div>
          <div className="border border-[#27272A] rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#050505] text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-wider">Filename</th>
                  <th className="text-center px-3 py-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-wider">Tune</th>
                  <th className="text-center px-3 py-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-wider">Rows</th>
                  <th className="text-center px-3 py-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-wider w-20">Hidden</th>
                </tr>
              </thead>
              <tbody>
                {visibleDatalogs.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-zinc-600">No datalogs</td></tr>
                )}
                {visibleDatalogs.map((d) => (
                  <tr key={d.id} className={`border-t border-[#1a1a1a] ${d.hidden ? 'opacity-40' : ''}`}>
                    <td className="px-3 py-2 font-['JetBrains_Mono'] text-zinc-300 truncate max-w-[280px]" title={d.filename}>
                      {d.filename}
                    </td>
                    <td className="px-3 py-2 text-center text-zinc-400">{d.tune_version || '—'}</td>
                    <td className="px-3 py-2 text-center text-zinc-400">{d.data_points ?? '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => patchDatalog(d.id, !d.hidden)}
                        disabled={savingId === d.id}
                        className={`p-1 rounded-sm hover:bg-[#1a1a1a] ${d.hidden ? 'text-zinc-500' : 'text-zinc-600 hover:text-zinc-300'}`}
                        data-testid={`curation-toggle-datalog-hidden-${d.id}`}
                        title={d.hidden ? 'Hidden — click to unhide' : 'Click to hide'}
                      >
                        {savingId === d.id ? <Loader2 className="w-4 h-4 animate-spin" />
                          : (d.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />)}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
